import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import LandingArticle from '@/components/LandingArticle';

const UPDATED = '2026-10-21';

const InlineCTA = () => (
  <div className="my-6 not-prose rounded-2xl border border-emerald-700/15 bg-emerald-50/60 p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid="inline-cta">
    <div className="text-sm text-emerald-900">
      <strong>Want turmeric without the black pepper?</strong>
      <span className="block text-emerald-800/80 mt-0.5">IIY Phytoventra BCM-95® uses turmeric&rsquo;s own essential oils — 100% piperine-free, vegan. £44.99 (first bottle £37.99), UK VAT &amp; delivery included.</span>
    </div>
    <Link to="/products/94056720-f372-4eef-bcfe-74063137a6e5" className="inline-flex shrink-0 items-center gap-1.5 bg-emerald-700 text-white font-bold py-2.5 px-4 rounded-full text-sm hover:bg-emerald-800 transition-colors" data-testid="hub-inline-cta">
      Shop BCM-95® <ArrowRight className="w-3.5 h-3.5" />
    </Link>
  </div>
);

const GuideLink = ({ to, children }) => (
  <Link to={to} className="text-emerald-700 font-semibold underline decoration-emerald-300 underline-offset-2 hover:decoration-emerald-600">{children}</Link>
);

const sections = [
  {
    id: 'quick-answer',
    heading: 'Is there a turmeric supplement without black pepper?',
    body: (
      <>
        <p><strong>Yes.</strong> Black pepper (piperine) is just one way to help the body absorb curcumin — it is not a requirement. Some of the most-studied curcumin extracts achieve high absorption <em>without</em> any pepper at all. If you prefer to avoid piperine, you have genuine, clean-label choices.</p>
        <p><strong>IIY Phytoventra BCM-95<sup>&reg;</sup></strong> is a fully <strong>piperine-free</strong> curcumin food supplement. Instead of black pepper, it uses the patented <strong>BCM-95<sup>&reg;</sup> / Curcugreen<sup>&reg;</sup></strong> system, which recombines curcumin with <strong>turmeric&rsquo;s own essential oils (turmerones)</strong> — so every component comes from the turmeric plant itself.</p>
        <InlineCTA />
      </>
    ),
  },
  {
    id: 'why-avoid',
    heading: 'Why some people prefer to avoid piperine',
    body: (
      <>
        <p>Piperine works by slowing the rate at which the liver and gut break curcumin down, which keeps more of it in circulation. That mechanism is also why some people choose to avoid it: <strong>piperine can affect how certain medicines are metabolised</strong> (via enzymes such as CYP3A4). If you take prescription medication, it is sensible to check with a pharmacist before using a black-pepper-based formula — or simply choose a piperine-free one.</p>
      </>
    ),
  },
  {
    id: 'how-bcm95-works',
    heading: 'How BCM-95® boosts absorption without pepper',
    body: (
      <>
        <p>Curcumin on its own is notoriously poorly absorbed. BCM-95<sup>&reg;</sup> solves this by reintegrating standardised curcuminoids with the turmeric plant&rsquo;s own volatile oils, which improve how much curcumin reaches the bloodstream. Published pharmacokinetic work (<strong>Antony et al., 2008</strong>) reported up to <strong>7× greater absorption than standard curcumin</strong> — achieved entirely without piperine or synthetic carriers.</p>
        <p>More on the formulation in <GuideLink to="/bcm-95-explained">BCM-95® Explained</GuideLink> and the wider comparison in <GuideLink to="/best-curcumin-supplement-uk">Best Curcumin Supplement UK</GuideLink>.</p>
      </>
    ),
  },
  {
    id: 'what-to-look-for',
    heading: 'What to look for in a piperine-free turmeric supplement',
    body: (
      <>
        <ul className="not-prose space-y-2.5 my-2">
          {[
            'A stated, named absorption method that is not piperine (e.g. turmeric essential oils, phospholipid or colloidal systems).',
            'A real curcumin figure per serving — BCM-95® delivers 650 mg curcumin per 2-capsule serving.',
            'A clean label — no black pepper, no synthetic carriers; vegan capsules and allergen declarations.',
            'A UK company with a VAT receipt, a real address and a clear returns policy.',
          ].map((t) => (
            <li key={t} className="flex items-start gap-2.5 text-slate-700">
              <Check className="w-4 h-4 mt-1 shrink-0 text-emerald-600" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
        <p>See also <GuideLink to="/turmeric-supplements-uk">Turmeric Supplements UK</GuideLink> and <GuideLink to="/vegan-turmeric-supplement-uk">Vegan Turmeric Supplement UK</GuideLink>.</p>
      </>
    ),
  },
  {
    id: 'faq',
    heading: 'Piperine-free turmeric — frequently asked questions',
    body: (
      <>
        <p className="font-semibold text-emerald-900">Does turmeric need black pepper to be absorbed?</p>
        <p>No. Black pepper (piperine) is one absorption aid, but formulations such as BCM-95<sup>&reg;</sup> use turmeric&rsquo;s own essential oils instead, so they are completely piperine-free while still being highly absorbable.</p>
        <p className="font-semibold text-emerald-900 mt-4">Why would I choose a turmeric supplement without piperine?</p>
        <p>Piperine can influence how some medicines are processed by the body. Choosing a piperine-free formula is a simple way to avoid that consideration, particularly if you take prescription medication — though you should still check with a pharmacist or GP.</p>
        <p className="font-semibold text-emerald-900 mt-4">Which UK turmeric supplement is piperine-free?</p>
        <p>IIY Phytoventra BCM-95<sup>&reg;</sup> is 100% piperine-free. It uses the patented Curcugreen<sup>&reg;</sup> turmeric-oil system, delivers 650&nbsp;mg curcumin per serving, and is sold directly in the UK at iiypv.com.</p>
      </>
    ),
  },
];

const faqLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    { q: 'Does turmeric need black pepper to be absorbed?', a: 'No. Black pepper (piperine) is one absorption aid, but formulations such as BCM-95® use turmeric\u2019s own essential oils instead, so they are completely piperine-free while still being highly absorbable.' },
    { q: 'Why would I choose a turmeric supplement without piperine?', a: 'Piperine can influence how some medicines are processed by the body. Choosing a piperine-free formula is a simple way to avoid that consideration, particularly if you take prescription medication — though you should still check with a pharmacist or GP.' },
    { q: 'Which UK turmeric supplement is piperine-free?', a: 'IIY Phytoventra BCM-95® is 100% piperine-free. It uses the patented Curcugreen® turmeric-oil system, delivers 650 mg curcumin per serving, and is sold directly in the UK at iiypv.com.' },
  ].map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
};

export default function TurmericWithoutBlackPepper() {
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />
      <LandingArticle
        title="Turmeric Supplement Without Black Pepper (Piperine-Free) UK"
        description="Yes — you can get a piperine-free turmeric supplement. How curcumin is absorbed without black pepper, why people avoid piperine, and the UK's clean-label BCM-95® option."
        keywords="turmeric without black pepper, turmeric supplement without piperine, piperine-free turmeric, curcumin without black pepper, pepper-free turmeric UK, BCM-95, Curcugreen, high absorption curcumin"
        pathOverride="/turmeric-without-black-pepper"
        heading="Turmeric Supplement Without Black Pepper (Piperine-Free)"
        subheading="Black pepper is only one way to absorb curcumin. Here's how a piperine-free turmeric supplement works, why people choose it, and the clean-label UK option."
        tag="Piperine-Free Turmeric"
        datePublished={UPDATED}
        dateModified={UPDATED}
        sections={sections}
      />
    </>
  );
}
